import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Settings,
  CreditCard,
  Package,
  MessageSquare,
  ChevronLeft,
  ChevronRight,
  User,
  LogOut,
  Users,
  BookOpenText,
  Rocket,
  TrendingUp,
  Target,
  Sliders,
  BarChart2,
  Code,
  History,
  Webhook,
  Download,
  Menu,
  X,
  Key,
  FlaskConical,
  Bell,
  ScrollText,
  Plug,
  Sun,
  Moon,
  Brain,
  GitBranch,
  Activity,
  Shield,
  Store,
  Crosshair,
  Paintbrush,
  FileText,
  ShieldAlert,
  Link2,
  Scan,
  BookOpen,
  DollarSign,
  MessageCircle,
} from "lucide-react";
import { useState, useEffect } from "react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { InlineVersion } from "@/components/ui/VersionDisplay";
import ConversionToast from "@/components/portal/ConversionToast";

const LOGO_FILTER_STYLE = {
  filter: "brightness(1.2) sepia(1) hue-rotate(100deg) saturate(2)",
};

const PortalLayout = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [menuReady, setMenuReady] = useState(false);
  const { logout, isAdmin, isLoading, profile, user } = useAuth();

  // ── Theme toggle ───────────────────────────────────────────
  const [isLightTheme, setIsLightTheme] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('intentdrive_theme') === 'light';
    }
    return false;
  });

  useEffect(() => {
    if (isLightTheme) {
      document.documentElement.classList.add('light-theme');
    } else {
      document.documentElement.classList.remove('light-theme');
    }
    localStorage.setItem('intentdrive_theme', isLightTheme ? 'light' : 'dark');
  }, [isLightTheme]);

  // On mount, apply saved preference
  useEffect(() => {
    const saved = localStorage.getItem('intentdrive_theme');
    if (saved === 'light') {
      document.documentElement.classList.add('light-theme');
      setIsLightTheme(true);
    }
  }, []);

  const toggleTheme = () => setIsLightTheme((prev) => !prev);

  useEffect(() => {
    if (profile && !isLoading) {
      setMenuReady(true);
    }
  }, [profile, isLoading]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setMenuReady(true);
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  // Sidebar repositioned for the Revenue Routing vocabulary.
  // "Routers" (flows) → "Analytics" (RPUS dashboard) → "Campaigns" (embed/slug) → settings
  const userMenuItems = [
    { icon: Target, label: "Routers", href: "/portal" },
    { icon: TrendingUp, label: "Analytics", href: "/portal/analytics" },
    { icon: DollarSign, label: "RPUS", href: "/portal/rpus" },
    { icon: Target, label: "Verticals", href: "/portal/verticals" },
    { icon: Target, label: "Offers", href: "/portal/offers" },
    { icon: Target, label: "Postbacks", href: "/portal/postbacks" },
    { icon: Sliders, label: "Scoring", href: "/portal/scoring" },
    { icon: Brain, label: "Scoring Rules", href: "/portal/scoring-rules" },
    { icon: GitBranch, label: "Decision Rules", href: "/portal/decision-rules" },
    { icon: Activity, label: "Outcomes", href: "/portal/outcomes" },
    { icon: Brain, label: "Learning", href: "/portal/learning" },
    { icon: BarChart2, label: "Performance", href: "/portal/performance" },
    { icon: History, label: "Sessions", href: "/portal/sessions" },
    { icon: Rocket, label: "Campaigns", href: "/portal/campaigns" },
    { icon: Users, label: "Affiliates", href: "/portal/affiliates" },
    { icon: Store, label: "Advertisers", href: "/portal/buyers" },
    { icon: Activity, label: "Buyers", href: "/portal/call-buyers" },
    { icon: Crosshair, label: "Attribution", href: "/portal/attribution" },
    { icon: FileText, label: "Landing Pages", href: "/portal/pages" },
    { icon: Link2, label: "Smart Links", href: "/portal/smart-links" },
    { icon: Scan, label: "Pixels", href: "/portal/pixels" },
    { icon: MessageCircle, label: "SMS", href: "/portal/sms" },
    { icon: Paintbrush, label: "White Label", href: "/portal/whitelabel" },
    { icon: Code, label: "Embed", href: "/portal/embed" },
    { icon: Webhook, label: "Webhooks", href: "/portal/webhooks" },
    { icon: Download, label: "Exports", href: "/portal/exports" },
    { icon: FlaskConical, label: "A/B Tests", href: "/portal/ab-tests" },
    { icon: Bell, label: "Notifications", href: "/portal/notifications" },
    { icon: ScrollText, label: "Audit Log", href: "/portal/audit" },
    { icon: Users, label: "Team", href: "/portal/team" },
    { icon: Key, label: "API Keys", href: "/portal/api-keys" },
    { icon: Plug, label: "Integrations", href: "/portal/integrations" },
    { icon: Plug, label: "LN360", href: "/portal/ln360" },
    { icon: ShieldAlert, label: "Fraud Detection", href: "/portal/fraud" },
    { icon: BookOpen, label: "Documentation", href: "/portal/docs" },
    { icon: Shield, label: "Security", href: "/portal/security" },
    { icon: CreditCard, label: "Billing", href: "/settings/billing" },
    { icon: Settings, label: "Settings", href: "/portal/settings" },
    { icon: User, label: "Profile", href: "/portal/profile" },
  ];

  const adminMenuItems = [
    { type: "separator" as const, label: "Admin Panel" },
    { icon: Users, label: "Users", href: "/admin/users" },
    { icon: Settings, label: "AI Settings", href: "/admin/ai-settings" },
    { icon: Package, label: "Plans", href: "/admin/plans" },
    {
      icon: BookOpenText,
      label: "Keys Dictionary",
      href: "/admin/fields-dictionary",
    },
  ];

  const shouldShowAdminMenu =
    menuReady && isAdmin && profile?.role === "admin";
  const allMenuItems = shouldShowAdminMenu
    ? [...userMenuItems, ...adminMenuItems]
    : userMenuItems;

  const handleLogout = async () => {
    const { success, error } = await logout();
    if (!success) {
      console.error("Logout failed:", error);
    } else {
      navigate("/");
    }
  };

  const displayName = profile?.name || profile?.email || user?.email || "User";
  const initials = (displayName || "U")
    .split(" ")
    .map((w) => w[0])
    .join("")
    .substring(0, 2)
    .toUpperCase();

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: 'var(--portal-bg, #020617)' }}>
      {/* Mobile hamburger bar */}
      <div className="md:hidden flex items-center gap-3 px-4 py-3 border-b border-slate-800" style={{ backgroundColor: 'var(--portal-card, #0f172a)' }}>
        <button
          type="button"
          onClick={() => setIsMobileOpen(true)}
          className="text-slate-300 hover:text-white"
        >
          <Menu className="h-5 w-5" />
        </button>
        <Link to="/portal" className="flex items-center gap-2">
          <img
            src="/logo.jpg"
            alt="IntentDrive.ai"
            className="h-8 w-auto rounded flex-shrink-0"
            style={LOGO_FILTER_STYLE}
          />
          <span className="font-bold text-sm text-white whitespace-nowrap">
            IntentDrive<span className="text-emerald-400">.ai</span>
          </span>
        </Link>
      </div>

      {/* Mobile sidebar backdrop */}
      {isMobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm md:hidden"
          onClick={() => setIsMobileOpen(false)}
        />
      )}

      <div className="flex flex-1">
        <div
          className={cn(
            "border-r border-slate-800 transition-all duration-300 ease-in-out relative flex flex-col",
            // Desktop: normal sidebar behavior
            "hidden md:flex",
            isCollapsed ? "w-16" : "w-64",
          )}
          style={{ backgroundColor: 'var(--portal-card, #0f172a)' }}
        >
          <Button
            variant="ghost"
            size="icon"
            className="absolute -right-3 top-6 h-6 w-6 rounded-full border border-slate-700 bg-slate-900 hover:bg-slate-800 text-slate-300 shadow-md z-10"
            onClick={() => setIsCollapsed(!isCollapsed)}
          >
            {isCollapsed ? (
              <ChevronRight className="h-4 w-4" />
            ) : (
              <ChevronLeft className="h-4 w-4" />
            )}
          </Button>

          {/* Logo */}
          <div className="p-4 border-b border-slate-800">
            <Link
              to="/portal"
              className={cn(
                "flex items-center gap-3",
                isCollapsed ? "justify-center" : "px-2"
              )}
            >
              <img
                src="/logo.jpg"
                alt="IntentDrive.ai"
                className="h-10 w-auto rounded flex-shrink-0"
                style={LOGO_FILTER_STYLE}
              />
              {!isCollapsed && (
                <span className="font-bold text-lg text-white whitespace-nowrap">
                  IntentDrive<span className="text-emerald-400">.ai</span>
                </span>
              )}
            </Link>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
            {allMenuItems.map((item, index) => {
              if ("type" in item && item.type === "separator") {
                return (
                  <div key={`separator-${index}`} className="py-2">
                    {!isCollapsed ? (
                      <>
                        <div className="border-t border-slate-800 my-2" />
                        <div className="px-3 py-1">
                          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                            {item.label}
                          </p>
                        </div>
                      </>
                    ) : (
                      <div className="border-t border-slate-800 my-2" />
                    )}
                  </div>
                );
              }

              // "Active" highlight: exact match OR the Routers button is
              // active while on any /portal or /portal/flow/* route.
              const isActive =
                location.pathname === item.href ||
                (item.href === "/portal" &&
                  (location.pathname === "/portal" ||
                    location.pathname.startsWith("/portal/flow") ||
                    location.pathname.startsWith("/portal/builder")));

              // Using a plain button with navigate() instead of <Link>.
              // Earlier: <Link><Button variant="ghost"> swallowed clicks on
              // some code paths because the nested button caught the event
              // before the Link's <a> could navigate. A direct onClick is
              // immune to that class of bug.
              return (
                <button
                  key={item.href}
                  type="button"
                  onClick={() => {
                    // Always navigate, even if the pathname matches. Forcing
                    // navigation guarantees components remount and data
                    // refetches — fixes the "click Routers from flow builder
                    // and nothing happens" bug.
                    navigate(item.href);
                  }}
                  className={cn(
                    "w-full flex items-center rounded-md px-3 py-2 text-sm transition-colors",
                    isActive
                      ? "bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/15"
                      : "text-slate-300 hover:bg-slate-800 hover:text-emerald-400",
                    isCollapsed ? "justify-center px-1" : "justify-start",
                  )}
                >
                  <item.icon className={cn("h-4 w-4", !isCollapsed && "mr-2")} />
                  {!isCollapsed && <span>{item.label}</span>}
                </button>
              );
            })}
          </nav>

          {/* User Profile Section */}
          <div className="border-t border-slate-800 p-3">
            <div
              className={cn(
                "flex items-center gap-3 mb-2",
                isCollapsed ? "justify-center" : "px-2"
              )}
            >
              <Avatar className="h-9 w-9 border border-slate-700 flex-shrink-0">
                <AvatarFallback className="bg-emerald-500/10 text-emerald-400 text-sm font-semibold">
                  {initials}
                </AvatarFallback>
              </Avatar>
              {!isCollapsed && (
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-white truncate">
                    {displayName}
                  </p>
                  {profile?.email && (
                    <p className="text-xs text-slate-500 truncate">
                      {profile.email}
                    </p>
                  )}
                </div>
              )}
            </div>
            <Button
              variant="ghost"
              className={cn(
                "w-full text-slate-300 hover:bg-slate-800 hover:text-amber-400",
                isCollapsed ? "justify-center px-1" : "justify-start text-sm"
              )}
              onClick={toggleTheme}
            >
              {isLightTheme ? (
                <Moon className={cn("h-4 w-4", !isCollapsed && "mr-2")} />
              ) : (
                <Sun className={cn("h-4 w-4", !isCollapsed && "mr-2")} />
              )}
              {!isCollapsed && <span>{isLightTheme ? 'Dark Mode' : 'Light Mode'}</span>}
            </Button>
            <Button
              variant="ghost"
              className={cn(
                "w-full text-slate-300 hover:bg-slate-800 hover:text-red-400",
                isCollapsed ? "justify-center px-1" : "justify-start text-sm"
              )}
              onClick={handleLogout}
            >
              <LogOut className={cn("h-4 w-4", !isCollapsed && "mr-2")} />
              {!isCollapsed && <span>Logout</span>}
            </Button>
          </div>
        </div>

        {/* Mobile Sidebar (overlay) */}
        <div
          className={cn(
            "fixed inset-y-0 left-0 z-50 w-64 border-r border-slate-800 flex flex-col transition-transform duration-300 ease-in-out md:hidden",
            isMobileOpen ? "translate-x-0" : "-translate-x-full"
          )}
          style={{ backgroundColor: 'var(--portal-card, #0f172a)' }}
        >
          <div className="p-4 border-b border-slate-800 flex items-center justify-between">
            <Link
              to="/portal"
              className="flex items-center gap-2"
              onClick={() => setIsMobileOpen(false)}
            >
              <img
                src="/logo.jpg"
                alt="IntentDrive.ai"
                className="h-10 w-auto rounded flex-shrink-0"
                style={LOGO_FILTER_STYLE}
              />
              <span className="font-bold text-lg text-white whitespace-nowrap">
                IntentDrive<span className="text-emerald-400">.ai</span>
              </span>
            </Link>
            <button
              type="button"
              onClick={() => setIsMobileOpen(false)}
              className="text-slate-400 hover:text-white"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
            {allMenuItems.map((item, index) => {
              if ("type" in item && item.type === "separator") {
                return (
                  <div key={`sep-m-${index}`} className="py-2">
                    <div className="border-t border-slate-800 my-2" />
                    <div className="px-3 py-1">
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                        {item.label}
                      </p>
                    </div>
                  </div>
                );
              }

              const isActive =
                location.pathname === item.href ||
                (item.href === "/portal" &&
                  (location.pathname === "/portal" ||
                    location.pathname.startsWith("/portal/flow") ||
                    location.pathname.startsWith("/portal/builder")));

              return (
                <button
                  key={`m-${item.href}`}
                  type="button"
                  onClick={() => {
                    navigate(item.href);
                    setIsMobileOpen(false);
                  }}
                  className={cn(
                    "w-full flex items-center rounded-md px-3 py-2 text-sm transition-colors justify-start",
                    isActive
                      ? "bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/15"
                      : "text-slate-300 hover:bg-slate-800 hover:text-emerald-400",
                  )}
                >
                  <item.icon className="h-4 w-4 mr-2" />
                  <span>{item.label}</span>
                </button>
              );
            })}
          </nav>

          <div className="border-t border-slate-800 p-3">
            <div className="flex items-center gap-3 mb-2 px-2">
              <Avatar className="h-9 w-9 border border-slate-700 flex-shrink-0">
                <AvatarFallback className="bg-emerald-500/10 text-emerald-400 text-sm font-semibold">
                  {initials}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">{displayName}</p>
                {profile?.email && (
                  <p className="text-xs text-slate-500 truncate">{profile.email}</p>
                )}
              </div>
            </div>
            <Button
              variant="ghost"
              className="w-full text-slate-300 hover:bg-slate-800 hover:text-amber-400 justify-start text-sm"
              onClick={() => { toggleTheme(); }}
            >
              {isLightTheme ? <Moon className="h-4 w-4 mr-2" /> : <Sun className="h-4 w-4 mr-2" />}
              <span>{isLightTheme ? 'Dark Mode' : 'Light Mode'}</span>
            </Button>
            <Button
              variant="ghost"
              className="w-full text-slate-300 hover:bg-slate-800 hover:text-red-400 justify-start text-sm"
              onClick={() => { handleLogout(); setIsMobileOpen(false); }}
            >
              <LogOut className="h-4 w-4 mr-2" />
              <span>Logout</span>
            </Button>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 overflow-x-hidden" style={{ backgroundColor: 'var(--portal-bg, #020617)', color: 'var(--portal-text, #ffffff)' }}>
          {children}
        </div>
      </div>

      {/* Footer */}
      <div className="border-t border-slate-800" style={{ backgroundColor: 'var(--portal-card, #0f172a)' }}>
        <div className="container mx-auto px-6 py-2">
          <div className="flex justify-between items-center text-xs text-slate-500">
            <div className="flex items-center gap-4">
              <p>© {new Date().getFullYear()} IntentDrive.ai</p>
              <InlineVersion />
            </div>
            <Link to="/" className="hover:text-emerald-400 transition-colors">
              Back to Home
            </Link>
          </div>
        </div>
      </div>
      <ConversionToast />
    </div>
  );
};

export default PortalLayout;
